import { urls as configUrls } from 'Reducers/config';
import { createSelector } from 'reselect';
import { getSelectedLang } from 'Selectors/getLanguages';
import { getType } from 'Reducers/helpers/typeModifier';
import { PLATFORM } from 'Constants/device.APP_TARGET';
import { isObject } from 'Helpers/objects';
import { USER_TYPE } from 'Constants/users';

export const BXP_REQUIRED_HEADER = { 'x-panamera-client-locationId': `web-${PLATFORM}` };
export const CMC_CITIES_URL = '/api/inspection/cities';

export const configSelector = data => {
    const type = getType('CONFIG', configUrls.getConfig);

    return data?.config?.collections[type];
};

export const parseLocale = locale => {
    let currentLocale = 'en';

    if (locale) {
        currentLocale = locale.split('.')[0].replace('_', '-');
    }

    /*
        HOT FIX

        On iOS, we discovered that 'hi-IN' made our price input fail because all numbers
        are translated to hindi characters
    */
    if (currentLocale === 'hi-IN') {
        currentLocale = 'en-IN';
    }

    return currentLocale;
};

export const getLocale = createSelector(
    configSelector,
    config => {
        return parseLocale(config && config.location && config.location.locale);
    }
);

export const getCountryName = createSelector(
    configSelector,
    config => {
        if (config
            && config.location
            && config.location.country
            && config.location.country.name) {
            return config.location.country.name;
        }
        return null;
    }
);

export const getCountryData = createSelector(
    configSelector,
    config => {
        if (config && config.location && config.location.country) {
            return {
                countryName: config.location.country.name,
                countryBrand: config.location.country.brand,
                countryIsoCode: config.location.country.iso_code
            };
        }
        return {};
    }
);

export const getSiteCode = createSelector(
    configSelector,
    config => {
        if (config
            && config.location
            && config.location.country
            && config.location.country.site_code) {
            return config.location.country.site_code;
        }
        return null;
    }
);

export const getCurrencyLocale = createSelector(
    configSelector,
    (_, props) => props.item.parameters && props.item.parameters.currency,
    (config, currency) => {
        if (currency) {
            const currenciesConf = config && config.location && config.location.currencies || [];
            const itemCurrencyNode = currenciesConf.find(item => item.iso_4217 === currency);

            return itemCurrencyNode && itemCurrencyNode.locale;
        }
        return null;
    }
);

export const getZendeskUrls = createSelector(
    configSelector,
    config => {
        if (config && config.location && config.location.country && config.location.country.zendesk_urls) {
            return config.location.country.zendesk_urls;
        }
        return null;
    }
);

export const getZendeskUrl = createSelector(
    configSelector,
    config => {
        if (config && config.location && config.location.country && config.location.country.zendesk_url) {
            return config.location.country.zendesk_url;
        }
        return null;
    }
);
export const getLocaleZendeskUrl = createSelector(
    getZendeskUrls,
    getZendeskUrl,
    getSelectedLang,
    (zendeskUrls, defaultUrl, lang) => {
        if (zendeskUrls) {
            const filterKey = Object.keys(zendeskUrls).filter(key => key.includes(lang));

            return zendeskUrls[filterKey];
        }
        return defaultUrl;
    }
);

export const getPrivacyUrl = createSelector(
    configSelector,
    config => {
        if (config && config.location && config.location.country && config.location.country.privacy_url) {
            return config.location.country.privacy_url;
        }
        return null;
    }
);

export const getLocalePrivacyUrl = createSelector(
    getPrivacyUrl,
    getSelectedLang,
    (privacyUrl, lang) => {
        if (privacyUrl) {
            const filterKey = Object.keys(privacyUrl).filter(key => key.includes(lang));

            return privacyUrl[filterKey];
        }
        return null;
    }
);

export const getSelectedLangCode = state => state.api && state.api.queryParam && state.api.queryParam.enableMultiLang && state.api.queryParam.lang;

export const getSEOLang = state => state.api && state.api.queryParam && state.api.queryParam.enableMultiLang && state.api.queryParam.SEOLangCode;

export const getAllCitiesSelector = state => state.bookAppointment && state.bookAppointment.cmcCities && state.bookAppointment.cmcCities.data;

export const getCMCCities = createSelector(
    getAllCitiesSelector,
    data => data && data.allCities || []
);

export const getVasConfigValues = createSelector(
    configSelector,
    getSelectedLang,
    getLocale,
    (_, vasValues) => vasValues,
    (_, __, showOlxAutosAdCardTag) => showOlxAutosAdCardTag,
    (_, __, ___, isNotFrmListing) => isNotFrmListing,
    (config, lang, locale, vasValues, showOlxAutosAdCardTag, isNotFrmListing) => {
        if (!config || !config.buyers) {
            return [];
        }

        const vasConfigValues = [];
        const langCode = lang || locale;
        const vas = config.buyers.features[0].value_added_services || {};
        let vasLangData = vas?.[langCode];
        const vasKeys = Object.keys(vas);

        if (vas && !vasLangData) {
            const matchedLangCode = vasKeys.find(key => key.includes(lang));

            vasLangData = matchedLangCode && vas[matchedLangCode];
        }

        if (!vasLangData) {
            vasLangData = vas[vasKeys[0]];
        }

        if (isNotFrmListing && vasValues.length === 0 && vasLangData) {
            return [];
        }

        vasValues.forEach(vasVal => {
            const vasTagVal = isObject(vasVal) ? vasVal.key : vasVal;

            if (vasTagVal === 'olx_autos' && !showOlxAutosAdCardTag) {
                return;
            }
            const val = vasLangData?.[vasTagVal];

            if (val) {
                vasConfigValues.push(val);
            }
        });

        return vasConfigValues;
    });

export const selectUspVasData = createSelector(
    configSelector,
    config => {
        if (config && config.buyers && config.buyers.features && Array.isArray(config.buyers.features) && config.buyers.features.length) {
            const features = config.buyers.features[0];

            return features?.usp_vas_data;
        }
        return null;
    }
);

export const selectBrandPromiseData = createSelector(
    configSelector,
    getSelectedLang,
    (_, { user }) => (user && user.dealer && user.dealer.car && user.dealer.car.dealer_type && user.dealer.car.dealer_type.toLowerCase()) || '',
    (config, lang, dealerType) => {
        if (config && config.buyers && config.buyers.features && Array.isArray(config.buyers.features) && config.buyers.features.length) {
            const features = config.buyers.features[0];
            const brandPromiseObject = features?.brand_promise;
            let brandPromiseLangData = brandPromiseObject?.[lang];

            if (brandPromiseObject && !brandPromiseLangData) {
                const brandPromiseLangs = Object.keys(brandPromiseObject);
                const matchedLangCode = brandPromiseLangs.find(key => key.includes(lang));

                brandPromiseLangData = matchedLangCode && brandPromiseObject[matchedLangCode];
            }

            if (brandPromiseLangData) {
                if ((dealerType in brandPromiseLangData)) {
                    return brandPromiseLangData[dealerType];
                }

                if ('default' in brandPromiseLangData) {
                    return brandPromiseLangData.default;
                }
            }
        }
        return null;
    }
);

export const selectBrandPromiseItems = createSelector(
    selectBrandPromiseData,
    (brandPromiseData = null) => {
        return (brandPromiseData && brandPromiseData.items && Array.isArray(brandPromiseData.items) && brandPromiseData.items.length) ? brandPromiseData.items : null;
    }
);

export const getCocoFofoConfigValue = createSelector(
    configSelector,
    getSelectedLangCode,
    getLocale,
    (_, item) => item,
    (config, lang, locale, { user_type = '', user_name = '' }) => {
        if (!config?.buyers) {
            return null;
        }

        const vas = config.buyers?.features?.[0]?.value_added_services || {};
        let allVasConfigValues = null;

        if (lang || locale) {
            allVasConfigValues = vas[lang] || vas[locale];
        }
        else {
            const vasKeys = Object.keys(vas);

            allVasConfigValues = vas[vasKeys[0]];
        }

        user_type = user_type.toLowerCase();
        const vasConfig = allVasConfigValues && allVasConfigValues[user_type];

        if (!vasConfig) {
            return null;
        }
        //
        if (user_type === USER_TYPE.COCO.toLowerCase()) {
            return vasConfig;
        }

        if (user_type === USER_TYPE.FOFO.toLowerCase()) {
            return {
                ...vasConfig,
                title: vasConfig.title.replace(/\$dealer_name/, user_name)
            };
        }

        return null;
    });

export const getCombinedVasValues = createSelector(
    getCocoFofoConfigValue,
    (state, item) => {
        const vasValues = item.vasTags || item.value_added_services || [];

        return getVasConfigValues(state, vasValues);
    },
    (_, __, showOlxAutosAdCardTag = true) => showOlxAutosAdCardTag,
    (cocoFofoConfigValue, vasConfigValues, showOlxAutosAdCardTag) => (cocoFofoConfigValue && showOlxAutosAdCardTag ? [
        ...vasConfigValues
    ] : vasConfigValues)
);

export const getAuthorisedDealerTxt = state => {
    const config = configSelector(state);
    const lang = getSelectedLangCode(state);
    const locale = getLocale(state);
    const brandTag = config.buyers?.features[0].brand_tag;

    if (!brandTag) {
        return null;
    }
    const firstKey = Object.keys(brandTag)[0];

    const brandTagLangVal = (brandTag[lang] || brandTag[locale] || brandTag[firstKey]);

    return brandTagLangVal ? brandTagLangVal.franchise.displayText : null;
};

export const getFeatureFlagConfig = flagName =>
    createSelector(configSelector, config => {
        if (config && typeof config === 'object' && Array.isArray(config.features)) {
            const result = config.features.find(item => item.name === flagName);

            return result || {};
        }

        return {};
    });

export const getEscrowConfig = getFeatureFlagConfig('frontend_escrow_flag');

export const getCouponConfig = getFeatureFlagConfig('coupons');

export const getMyBenefitsConfig = getFeatureFlagConfig('frontend_my_benefits_flag');

export const getFeatureConfigByName = createSelector(
    configSelector,
    (_, featureKey) => featureKey,
    (config, featureKey) => {
        if (config && typeof config === 'object' && Array.isArray(config.features)) {
            const result = config.features.find(item => item.name === featureKey);

            return !!(result && result.enabled);
        }

        return false;
    }
);

export const getDiscountTagConfig = createSelector(
    configSelector,
    config => {
        const key = 'frontend_discount_tag_flag';

        if (config && typeof config === 'object' && Array.isArray(config.features)) {
            const result = config.features.find(item => item.name === key);

            return result || {};
        }

        return {};
    }
);
